import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthenticatedRequest } from "@/hooks/use-authenticated-request";
import AppLayout from "@/components/layout/app-layout";
import { Tenant } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Palette, Lock, Settings2, PlusCircle, Settings } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogClose
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as z from "zod";
import CreateTenantModal from "@/components/tenants/create-tenant-modal"; // Import the new modal

// Update tenant form schema
const updateTenantSchema = z.object({
  companyName: z.string().min(2, "Company name must be at least 2 characters"),
  baseCurrency: z.string().min(3, "Currency code must be at least 3 characters"),
});
type UpdateTenantFormValues = z.infer<typeof updateTenantSchema>;

// Invite user to tenant form schema
const inviteUserToTenantSchema = z.object({
  email: z.string().email("Invalid email address"),
  roleToAssign: z.enum(["TenantAdmin", "Reviewer"], { required_error: "Please select a role." }),
});
type InviteUserToTenantFormValues = z.infer<typeof inviteUserToTenantSchema>;

interface InvitationCreationResponse {
  message: string;
  invitationId?: string;
}

export default function TenantManagementPage() {
  const { user: currentUser } = useAuth();
  const authenticatedRequest = useAuthenticatedRequest();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("general");
  const [isInviteUserDialogOpen, setIsInviteUserDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCreateTenantModalOpen, setIsCreateTenantModalOpen] = useState(false); // State for the new modal

  // Determine which tenant ID to use based on user type and context
  const activeTenantId = useMemo(() => {
    // For regular tenant users, use their assigned tenant
    if (!currentUser?.isSuperAdmin) {
      return currentUser?.tenantId;
    }
    
    // For SuperAdmins, use their selected tenant context if it's a specific tenant
    if (currentUser.activeTenantContext && 
        currentUser.activeTenantContext !== '__ALL__' && 
        currentUser.activeTenantContext !== 'null') {
      return currentUser.activeTenantContext;
    }
    
    // SuperAdmin hasn't selected a specific tenant
    return null;
  }, [currentUser]);

  // Fetch all tenants data (for SuperAdmin "All Tenants" view)
  // This hook is called unconditionally at the top level.
  // The 'enabled' option controls whether the query actually runs.
  const { data: allTenants, isLoading: isLoadingAllTenants } = useQuery<Tenant[]>({
    queryKey: ["/api/tenants/list-for-admin"],
    queryFn: async () => {
      const response = await authenticatedRequest('/api/tenants/list-for-admin');
      if (!response.ok) {
        throw new Error("Failed to fetch all tenants for admin view");
      }
      return response.json();
    },
    enabled: !!currentUser?.isSuperAdmin && currentUser.activeTenantContext === '__ALL__',
  });

  // Fetch tenant data (for general settings)
  const { 
    data: tenant, 
    isLoading: isLoadingTenantDetails, 
    error: tenantDetailsError 
  } = useQuery<Tenant>({
    queryKey: ["/api/tenant/settings", activeTenantId],
    queryFn: async () => {
      if (!activeTenantId) throw new Error("Tenant ID not available");
      
      console.log("[TenantManagementPage] Fetching tenant data for ID:", activeTenantId);
      
      // Fetch tenant management using the appropriate API endpoint
      const response = await authenticatedRequest(`/api/tenant/settings`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[TenantManagementPage] Error fetching tenant data: ${response.status} ${response.statusText}`, errorText);
        
        // Handle specific error cases
        if (response.status === 403) {
          console.log("[TenantManagementPage] 403 Forbidden detected");
          throw new Error("ACCESS_DENIED_403");
        }
        
        throw new Error(`Failed to fetch tenant details: ${response.statusText}`);
      }
      
      let data;
      try {
        data = await response.json();
      } catch (e) {
        console.error("[TenantManagementPage] Error parsing JSON response:", e);
        const textResponse = await response.text(); // Attempt to get text response for logging
        console.error("[TenantManagementPage] Non-JSON response text:", textResponse);
        throw new Error("Failed to parse tenant details from API response.");
      }
      
      console.log("[TenantManagementPage] Tenant data received:", data);
      return data;
    },
    enabled: !!currentUser && !!activeTenantId && activeTab === 'general',
    retry: (failureCount, error) => {
      // Don't retry on 403 errors
      if (error?.message === "ACCESS_DENIED_403") {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Form for general tenant management
  const tenantDetailsForm = useForm<UpdateTenantFormValues>({
    resolver: zodResolver(updateTenantSchema),
    defaultValues: {
      companyName: "",
      baseCurrency: "USD",
    },
  });

  // Add useEffect to update form when tenant data is loaded/changed
  useEffect(() => {
    if (tenant) {
      tenantDetailsForm.reset({
        companyName: tenant.companyName || "",
        baseCurrency: tenant.baseCurrency || "USD",
      });
    }
  }, [tenant, tenantDetailsForm.reset]);

  // Form for inviting user to tenant
  const inviteUserForm = useForm<InviteUserToTenantFormValues>({
    resolver: zodResolver(inviteUserToTenantSchema),
    defaultValues: {
      email: "",
      roleToAssign: "Reviewer", // Default role
    },
  });

  // Update tenant mutation (General Settings)
  const updateTenantMutation = useMutation({
    mutationFn: async (data: UpdateTenantFormValues) => {
      if (!activeTenantId) throw new Error("Tenant ID missing");
      
      console.log("[TenantManagementPage] Updating tenant:", activeTenantId, data);
      
      const response = await authenticatedRequest(`/api/tenant/settings`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[TenantManagementPage] Error updating tenant: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`Failed to update tenant: ${response.statusText}`);
      }
      
      return await response.json();
    },
    onSuccess: () => {
      toast({
        title: "Settings updated",
        description: "Your tenant management have been successfully updated.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/tenant/settings", activeTenantId] });
      // Invalidate the query used by TopNav to refresh the tenant list
      queryClient.invalidateQueries({ queryKey: ["/api/tenants/list-for-admin", currentUser?.userId] });
      setIsEditMode(false); // Exit edit mode after saving
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update settings",
        description: error.message || "An error occurred while updating settings.",
        variant: "destructive",
      });
    },
  });

  // Invite user to tenant mutation
  const inviteUserToTenantMutation = useMutation<InvitationCreationResponse, Error, InviteUserToTenantFormValues>({
    mutationFn: async (data) => {
      if (!activeTenantId) throw new Error("Current user or tenant ID is missing.");

      const response = await authenticatedRequest("/api/invitations/create", {
        method: "POST",
        body: JSON.stringify({ 
          email: data.email,
          tenantId: activeTenantId, // Implicitly use current user's tenantId
          roleToAssign: data.roleToAssign
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to send invitation");
      }
      return response.json(); 
    },
    onSuccess: (response) => {
      toast({ title: "Invitation Sent", description: response.message });
      setIsInviteUserDialogOpen(false);
      inviteUserForm.reset();
      // Optionally, invalidate a query for tenant users list if it exists
      // queryClient.invalidateQueries({ queryKey: ["/api/tenants", activeTenantId, "users"] });
    },
    onError: (error) => {
      toast({ title: "Invitation Error", description: error.message, variant: "destructive" });
    },
  });

  // Submit handler for general tenant management
  function onSubmitTenantDetails(data: UpdateTenantFormValues) {
    updateTenantMutation.mutate(data);
  }

  // Submit handler for inviting user to tenant
  function onInviteUserSubmit(values: InviteUserToTenantFormValues) {
    inviteUserToTenantMutation.mutate(values);
  }
  
  // Placeholder: Fetch users for the current tenant (for the Users tab)
  // const { data: tenantUsers, isLoading: isLoadingTenantUsers } = useQuery<AppUser[]>({
  //   queryKey: ["/api/tenants", activeTenantId, "users"],
  //   queryFn: async () => { /* ... fetch logic ... */ return []; },
  //   enabled: !!activeTenantId && activeTab === 'users',
  // });

  // --- START MODIFIED SECTION FOR SUPERADMIN VIEWS ---
  // Add console.log for debugging tenant object
  console.log("[TenantManagementPage] Rendering with tenant data:", tenant);

  const handleTenantCreated = () => {
    // Refetch the list of all tenants
    queryClient.invalidateQueries({ queryKey: ["/api/tenants/list-for-admin"] });
    // The tenant selector in the header should ideally update due to revalidation
    // triggered by the API route. If not, further state management for the header might be needed.
    // For now, we rely on the server-side revalidation.
  };

  if (currentUser?.isSuperAdmin) {
    if (currentUser.activeTenantContext === '__ALL__') {
      // Displaying the table for "All Tenants"
      // The useQuery for 'allTenants' is now at the top level.
      return (
        <AppLayout>
          <div className="space-y-6 p-6">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-semibold">All Tenants</h1>
              <Button onClick={() => setIsCreateTenantModalOpen(true)}>
                <PlusCircle className="mr-2 h-4 w-4" /> Create Tenant
              </Button>
            </div>
            {isLoadingAllTenants ? (
              <p>Loading tenants...</p>
            ) : allTenants && allTenants.length > 0 ? (
              <Card>
                <CardContent className="p-0">
                  {/* Basic Table Placeholder - Replace with actual ShadCN Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company Name</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant ID</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {allTenants.map((t) => (
                          <tr key={t.tenantId}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{t.companyName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.tenantId}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.createdAt ? new Date(t.createdAt).toLocaleDateString() : 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <p>No tenants found.</p>
            )}
          </div>
          <CreateTenantModal
            isOpen={isCreateTenantModalOpen}
            onClose={() => setIsCreateTenantModalOpen(false)}
            onTenantCreated={handleTenantCreated}
          />
        </AppLayout>
      );
    } else if (currentUser.activeTenantContext === 'null' || !currentUser.activeTenantContext) {
      // This is the "No Tenant" selected view for SuperAdmin
      return (
        <AppLayout>
          <div className="p-6">
            <h1 className="text-3xl font-bold mb-8">Tenant Management</h1>
            <Card>
              <CardContent className="p-8 flex flex-col items-center justify-center min-h-[300px]">
                <Settings2 className="h-16 w-16 text-muted-foreground opacity-75 mb-6" />
                <h2 className="text-xl font-semibold mb-3">No Tenant Selected</h2>
                <p className="text-center text-muted-foreground max-w-md">
                  Please select a tenant from the dropdown in the top navigation bar to view its specific settings, 
                  or choose "All Tenants" to see an overview and manage all tenants.
                </p>
              </CardContent>
            </Card>
          </div>
        </AppLayout>
      );
    }
    // If a specific tenant IS selected by SuperAdmin, activeTenantId will be set,
    // and the logic below for displaying individual tenant management will take over.
  }
  // --- END MODIFIED SECTION FOR SUPERADMIN VIEWS ---

  // Show message when SuperAdmin hasn't selected a specific tenant
  if (!activeTenantId && (!currentUser?.isSuperAdmin || 
                         (currentUser?.isSuperAdmin && 
                          currentUser.activeTenantContext !== '__ALL__' && 
                          currentUser.activeTenantContext !== 'null' && 
                          currentUser.activeTenantContext) 
                        )
     ) {
    // This case should ideally not be hit if a non-superadmin always has a tenantId,
    // or if a superadmin has selected a specific tenant (which would set activeTenantId).
    // This is a fallback for unexpected states.
    return (
      <AppLayout>
        <div className="p-6">
          <h1 className="text-2xl font-bold mb-8">Tenant Management</h1>
          <Card>
            <CardContent className="p-8 flex flex-col items-center justify-center">
              <Settings className="h-16 w-16 text-muted mb-4" />
              <h2 className="text-xl font-semibold mb-2">Tenant Not Available</h2>
              <p className="text-center text-muted-foreground mb-6">
                The tenant context could not be determined. Please contact support if this issue persists.
              </p>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  // Handle 403 Forbidden errors (access denied)
  if (tenantDetailsError) {
    // Check for 403 errors in multiple ways since React Query might format errors differently
    const is403Error = tenantDetailsError.message === "ACCESS_DENIED_403" || 
                       tenantDetailsError.message === "FORBIDDEN" || 
                       tenantDetailsError.message?.includes("403") ||
                       tenantDetailsError.message?.includes("Forbidden") ||
                       tenantDetailsError.message?.includes("Only admins can access tenant management");
    
    if (is403Error) {
      return (
        <AppLayout>
          <div className="p-6">
            <h1 className="text-2xl font-bold mb-8">Tenant Management</h1>
            <Card>
              <CardContent className="p-8 flex flex-col items-center justify-center">
                <Lock className="h-16 w-16 text-destructive mb-4" />
                <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
                <p className="text-center text-muted-foreground mb-6">
                  You don't have permission to access tenant management. Only tenant administrators can view and modify these settings.
                </p>
                <Button variant="outline" onClick={() => window.history.back()}>
                  Go Back
                </Button>
              </CardContent>
            </Card>
          </div>
        </AppLayout>
      );
    }
  }

  if (isLoadingTenantDetails && activeTab === 'general' && activeTenantId) {
    return (
      <AppLayout>
        <div className="p-6">
          <h1 className="text-2xl font-bold mb-8">Tenant Management</h1>
          <div className="bg-muted animate-pulse rounded-md h-40 w-full" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6">
        <h1 className="text-3xl font-bold mb-2">Tenant Management</h1>
        <p className="text-muted-foreground mb-8">Manage your organization&apos;s profile, users, and settings.</p>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="overflow-x-auto pb-2">
            <TabsList className="w-full flex flex-nowrap md:grid md:grid-cols-4 gap-2 min-w-[500px] md:min-w-0">
              <TabsTrigger value="general" className="flex items-center justify-center whitespace-nowrap">
                <Settings2 className="mr-2 h-4 w-4" />
                <span>General</span>
              </TabsTrigger>
              <TabsTrigger value="users" className="flex items-center justify-center whitespace-nowrap">
                <Users className="mr-2 h-4 w-4" />
                <span>Users & Invitations</span>
              </TabsTrigger>
              <TabsTrigger value="branding" className="flex items-center justify-center whitespace-nowrap" disabled>
                <Palette className="mr-2 h-4 w-4" />
                <span>Branding (Soon)</span>
              </TabsTrigger>
              <TabsTrigger value="security" className="flex items-center justify-center whitespace-nowrap" disabled>
                <Lock className="mr-2 h-4 w-4" />
                <span>Security (Soon)</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* General Settings Tab */}
          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>General Information</CardTitle>
                <CardDescription>Update your company&apos;s name and basic details.</CardDescription>
              </CardHeader>
              <Form {...tenantDetailsForm}>
                <form onSubmit={tenantDetailsForm.handleSubmit(onSubmitTenantDetails)}>
                  <CardContent className="space-y-6">
                    <FormField
                      control={tenantDetailsForm.control}
                      name="companyName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Company Name</FormLabel>
                          <FormControl>
                            {isEditMode ? (
                              <Input placeholder="Your Company Inc." {...field} />
                            ) : (
                              <div className="mt-1 p-2 border rounded-md bg-muted/50 text-sm min-h-[38px]">
                                {tenant?.companyName || 'N/A'}
                              </div>
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={tenantDetailsForm.control}
                      name="baseCurrency"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Base Currency</FormLabel>
                          <FormControl>
                            {isEditMode ? (
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select currency" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                                  <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                                  <SelectItem value="GBP">GBP - British Pound</SelectItem>
                                  <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                                  <SelectItem value="JPY">JPY - Japanese Yen</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <div className="mt-1 p-2 border rounded-md bg-muted/50 text-sm min-h-[38px]">
                                {tenant?.baseCurrency || 'USD'} - {
                                  {
                                    'USD': 'US Dollar',
                                    'CAD': 'Canadian Dollar', 
                                    'EUR': 'Euro',
                                    'GBP': 'British Pound',
                                    'AUD': 'Australian Dollar',
                                    'JPY': 'Japanese Yen'
                                  }[tenant?.baseCurrency || 'USD'] || 'Unknown Currency'
                                }
                              </div>
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="pt-4">
                      <h3 className="text-lg font-medium mb-4">Tenant Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">Tenant ID</Label>
                          <div className="mt-1 p-2 border rounded-md bg-muted/50 text-sm break-all">
                            {tenant?.tenantId || 'N/A'}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            For reference only.
                          </p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Created On</Label>
                          <div className="mt-1 p-2 border rounded-md bg-muted/50 text-sm">
                            {tenant?.createdAt ? new Date(tenant.createdAt).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="border-t px-6 py-4">
                    {isEditMode ? (
                      <div className="flex space-x-2">
                        <Button 
                          type="submit" 
                          disabled={updateTenantMutation.isPending || !tenantDetailsForm.formState.isDirty}
                        >
                          {updateTenantMutation.isPending ? "Saving..." : "Save Changes"}
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => {
                            setIsEditMode(false);
                            // Reset form to original values if tenant data exists
                            if (tenant) {
                              tenantDetailsForm.reset({ 
                                companyName: tenant.companyName || "",
                                baseCurrency: tenant.baseCurrency || "USD"
                              });
                            }
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button onClick={() => setIsEditMode(true)} disabled={!tenant}>Edit</Button>
                    )}
                  </CardFooter>
                </form>
              </Form>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <div>
                  <CardTitle className="text-xl">User Management</CardTitle>
                  <CardDescription className="mt-1.5">
                    Invite and manage users who have access to your tenant account.
                  </CardDescription>
                </div>
                <Dialog open={isInviteUserDialogOpen} onOpenChange={setIsInviteUserDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <PlusCircle className="mr-2 h-4 w-4" /> Invite User
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>Invite User to Tenant</DialogTitle>
                      <DialogDescription>
                        Enter the email and select a role for the new user.
                      </DialogDescription>
                    </DialogHeader>
                    <Form {...inviteUserForm}>
                      <form onSubmit={inviteUserForm.handleSubmit(onInviteUserSubmit)} className="space-y-4 py-4">
                        <FormField
                          control={inviteUserForm.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email Address</FormLabel>
                              <FormControl>
                                <Input type="email" placeholder="user@example.com" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={inviteUserForm.control}
                          name="roleToAssign"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Role</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select a role" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="Reviewer">Reviewer</SelectItem>
                                  <SelectItem value="TenantAdmin">Tenant Admin</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <DialogFooter className="pt-4">
                           <DialogClose asChild>
                                <Button type="button" variant="outline">Cancel</Button>
                           </DialogClose>
                          <Button type="submit" disabled={inviteUserToTenantMutation.isPending}>
                            {inviteUserToTenantMutation.isPending ? "Sending Invitation..." : "Send Invitation"}
                          </Button>
                        </DialogFooter>
                      </form>
                    </Form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="py-6">
                {/* Placeholder for listing tenant users */}
                <div className="text-center py-10 border-2 border-dashed border-muted rounded-lg">
                  <Users className="mx-auto h-12 w-12 text-muted-foreground opacity-50 mb-4" />
                  <h3 className="text-lg font-medium mb-2">Tenant User List</h3>
                  <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                    A list of users within this tenant will be displayed here soon.
                  </p>
                  {/* <Button variant="outline" disabled>View All Users (Coming Soon)</Button> */}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branding Tab Content (Placeholder) */}
          <TabsContent value="branding">
             <Card>
                <CardHeader>
                    <CardTitle>Branding</CardTitle>
                    <CardDescription>Customize the look and feel of your tenant account (Coming Soon).</CardDescription>
                </CardHeader>
                <CardContent className="text-center py-10">
                    <Palette className="mx-auto h-12 w-12 text-muted-foreground opacity-50 mb-4" />
                    <p className="text-muted-foreground">Branding options will be available here.</p>
                </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab Content (Placeholder) */}
          <TabsContent value="security">
            <Card>
                <CardHeader>
                    <CardTitle>Security Settings</CardTitle>
                    <CardDescription>Manage security options for your tenant (Coming Soon).</CardDescription>
                </CardHeader>
                <CardContent className="text-center py-10">
                    <Lock className="mx-auto h-12 w-12 text-muted-foreground opacity-50 mb-4" />
                    <p className="text-muted-foreground">Advanced security settings will be available here.</p>
                </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}